import React, { useEffect, useState } from "react";
import Header from "../../../../Componets/Header";
import Status from "../../../../Componets/Status";
import Table from "../../../../Componets/Table";
import toast from "react-hot-toast";
import tables from "../../../../Data/Tables/Customers/Statement/Walk-In/tables";
import headers from "../../../../Data/Tables/Customers/Statement/Walk-In/headers";
import Loadercomponent from "../../../../Componets/Loader";
import { get_all_invoices } from "../../../../Controllers/Sales/Invoices";
import DateTimeBadge from "../../../../Componets/DateTime/DateTimeBadge";
import UserCurrency from "../../../../Componets/UserCurrency";

const WalkInCustomersReport = () => {
  const [loader, setLoader] = useState(false);
  const [header, setHeader] = useState(headers);
  const [body, setBody] = useState([]);
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalInvoices: 0,
    totalOutstanding: 0,
    averageTransaction: 0,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
  });

  const paginationdata = (data) => {
    let headerdata = {};
    header?.components?.map((value) => {
      headerdata[value.name] = value.value;
    });

    fetchData({
      ...data,
      search: headerdata?.search,
      payment: headerdata?.payment,
      date: headerdata?.date,
    });
  };

  const formdata = (data) => {
    let page = data?.search ? pagination?.currentPage : 1;
    fetchData({
      ...data,
      currentPage: page,
      totalCount: pagination?.totalCount,
      totalPages: pagination?.totalPages,
    });
  };

  const calculateOutstanding = (invoice) => {
    const total = invoice.total || 0;
    const paid = calculateTotalPaid(invoice);
    return total - paid;
  };

  const calculateTotalPaid = (invoice) => {
    if (!invoice.payments || !Array.isArray(invoice.payments)) return 0;
    return invoice.payments.reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );
  };

  const getPaymentStatus = (invoice) => {
    const outstanding = calculateOutstanding(invoice);
    const totalPaid = calculateTotalPaid(invoice);

    if (outstanding <= 0) return { status: 2, label: "Paid" };
    if (totalPaid > 0) return { status: 3, label: "Partial" };
    return { status: 1, label: "Pending" };
  };

  const getDeliveryStatus = (invoice) => {
    const statusMap = {
      1: "Pending",
      2: "Delivered",
      3: "Shipped",
    };
    return {
      status: invoice.delivery_status,
      label: statusMap[invoice.delivery_status] || "Pending",
    };
  };

  const getPaymentMethods = (invoice) => {
    if (!invoice.payments || !Array.isArray(invoice.payments))
      return "No payments";

    const methods = invoice.payments.map((payment) => payment.name);
    return methods.join(", ");
  };

  const fetchData = async (tabledata) => {
    try {
      setLoader(true);

      const params = {
        search: tabledata?.search,
        payment: tabledata?.payment,
        date: tabledata?.date,
        page: tabledata?.currentPage || 1,
        source: "Walk-In",
        limit: 10,
      };

      const response = await get_all_invoices(params);

      if (response?.status) {
        let invoices = response?.data?.data || [];

        invoices = invoices.filter(
          (invoice) => calculateOutstanding(invoice) > 0
        );

        const startIndex = (params.page - 1) * params.limit;
        const paginatedInvoices = invoices.slice(
          startIndex,
          startIndex + params.limit
        );

        let totalRevenue = 0;
        let totalOutstanding = 0;

        const tableData = paginatedInvoices.map((invoice) => {
          const outstanding = calculateOutstanding(invoice);
          const totalPaid = calculateTotalPaid(invoice);
          const paymentStatus = getPaymentStatus(invoice);
          const deliveryStatus = getDeliveryStatus(invoice);
          const paymentMethods = getPaymentMethods(invoice);

          totalRevenue += invoice.total || 0;
          totalOutstanding += outstanding;

          return {
            path: `/app/billing/view/${invoice?._id}`,
            "Invoice No": `#INV-${invoice.number}`,
            Date: <DateTimeBadge date={invoice?.date} />,
            "Total Amount": <> <UserCurrency /> {(invoice.total || 0).toFixed(3)}</>,
            "Paid Amount": <><UserCurrency /> {totalPaid.toFixed(3)}</>,
            Outstanding: <><UserCurrency/> {outstanding.toFixed(3)}</>,
            "Payment Status":
              invoice?.payment_status == 2 ? (
                <span className="bg-green-100 text-green-600 text-sm px-2 py-1 rounded-lg">
                  Paid
                </span>
              ) : invoice?.payment_status == 1 ? (
                <span className="bg-yellow-100 text-yellow-600 text-sm px-2 py-1 rounded-lg">
                  Partial
                </span>
              ) : (
                <span className="bg-red-100 text-red-600 text-sm px-2 py-1 rounded-lg">
                  Unpaid
                </span>
              ),
            "Delivery Status":
              invoice?.delivery_status == 2 ? (
                <span className="bg-green-100 text-green-600 text-sm px-2 py-1 rounded-lg">
                  Delivered
                </span>
              ) : invoice?.delivery_status == 1 ? (
                <span className="bg-yellow-100 text-yellow-600 text-sm px-2 py-1 rounded-lg">
                  Partial
                </span>
              ) : (
                <span className="bg-red-100 text-red-600 text-sm px-2 py-1 rounded-lg">
                  Pending
                </span>
              ),
            "Payment Methods": paymentMethods,
          };
        });

        setBody(tableData);

        setSummary({
          totalRevenue: totalRevenue,
          totalInvoices: invoices.length,
          totalOutstanding: totalOutstanding,
          averageTransaction:
            invoices.length > 0 ? totalRevenue / invoices.length : 0,
        });

        setPagination({
          currentPage: params.page,
          totalCount: invoices.length,
          totalPages: Math.ceil(invoices.length / params.limit),
        });
      } else {
        toast.error(
          response?.message || "Failed to fetch walk-in customers report"
        );
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoader(false);
    }
  };

  const headerReset = () => {
    let componentData = [];
    headers?.components?.map((value) => {
      componentData?.push({
        ...value,
        value: null,
      });
    });
    setHeader({ ...headers, components: componentData });
  };

  useEffect(() => {
    fetchData();
    headerReset();
  }, []);

  return (
    <div>
      <Header headers={header} setHeaders={setHeader} formdata={formdata}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 mt-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className=" text-gray-700 mb-2">Total Revenue</h3>
            <p className=" text-orange-600">
              <UserCurrency /> {summary.totalRevenue.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className=" text-gray-700 mb-2">Total Invoices</h3>
            <p className=" text-green-600">{pagination.totalCount}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className=" text-gray-700 mb-2">Outstanding</h3>
            <p className=" text-red-600">
              <UserCurrency /> {summary.totalOutstanding.toFixed(3)}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className=" text-gray-700 mb-2">Avg. Transaction</h3>
            <p className=" text-orange-600">
              <UserCurrency /> {summary.averageTransaction.toFixed(3)}
            </p>
          </div>
        </div>
        <Loadercomponent loader={loader}>
          <Table
            head={tables}
            body={body}
            pagination={pagination}
            paginationdata={paginationdata}
            summary={2}
          />
        </Loadercomponent>
      </Header>
    </div>
  );
};

export default WalkInCustomersReport;
